import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import {
  BdShareablesService,
  SharePreview,
  SharePreviewResource,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import { Checkbox } from 'primeng/checkbox';
import { TreeNode } from 'primeng/api';
import { TreeTableModule } from 'primeng/treetable';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { forkJoin, take } from 'rxjs';

import { GrantConfig, GrantSelection, SelectedItem } from '../../dialog.models';
import {
  BD_SHARE_PERMISSION_DISPLAY_NAME,
  BdSharePermissionHelper,
  SHAREABLE_TYPE_DISPLAY_NAME,
} from '@bindoc-projects/resource-domain-lib';

interface PreviewNodeData {
  /** Stable selection key, unique per resource (`type::id`). */
  key: string;
  id: string;
  type: ShareableType;
  name: string;
  typeLabel: string;
  permissionLabel: string;
  /** True when the resource has no permission yet and a grant is required. */
  needsGrant: boolean;
  /** True for the originally selected item, false for a dependency. */
  isRoot: boolean;
  /** True when the resource has an interactive checkbox and counts towards selection. */
  actionable: boolean;
  /**
   * Selection keys aggregated by a root row's checkbox: the root itself plus
   * its actionable dependencies. Empty for dependency rows.
   */
  groupKeys: string[];
}

function nodeKey(type: ShareableType, id: string): string {
  return `${type}::${id}`;
}

@Component({
  selector: 'bd-grant-preview-step',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-3">
      @if (errorState() !== 'none') {
        <div class="flex items-start gap-3" data-test="grant-preview-error">
          <i
            class="pi pi-lock text-2xl text-bd-warning"
            [class.pi-lock]="errorState() === 'forbidden'"
            [class.pi-exclamation-triangle]="errorState() === 'error'"
          ></i>
          <div class="flex flex-col gap-1">
            <p class="m-0 font-medium">
              @if (errorState() === 'forbidden') {
                Sie haben keine Berechtigung, diese Ressource freizugeben.
              } @else {
                Die Freigabe-Vorschau konnte nicht geladen werden.
              }
            </p>
            <p class="m-0 text-sm text-muted-color">
              @if (errorState() === 'forbidden') {
                Zum Freigeben benötigen Sie die volle Berechtigung (Löschen) auf
                dieser Ressource. Wenden Sie sich an die Person, die sie mit
                Ihnen geteilt hat.
              } @else {
                Bitte versuchen Sie es später erneut.
              }
            </p>
          </div>
        </div>
      } @else {
        <span
          class="text-sm text-surface-600"
          data-test="grant-preview-selection-count"
        >
          {{ selectedCount() }} von {{ totalCount() }} Ressourcen ausgewählt
        </span>
        <p-treetable [value]="nodes()" stripedRows>
          <ng-template #header>
            <tr>
              <th style="width: 3rem">
                <p-checkbox
                  [ngModel]="allSelected()"
                  [binary]="true"
                  [indeterminate]="masterIndeterminate()"
                  [disabled]="totalCount() === 0"
                  (onChange)="toggleAll()"
                  ariaLabel="Alle Ressourcen auswählen"
                  data-test="grant-preview-select-all"
                />
              </th>
              <th>Ressource</th>
              <th class="w-1/4">Status</th>
            </tr>
          </ng-template>
          <ng-template #body let-rowNode let-rowData="rowData">
            <tr [ttRow]="rowNode">
              <td style="width: 3rem">
                @if (rowData.actionable) {
                  @if (rowData.isRoot) {
                    <p-checkbox
                      [ngModel]="isGroupChecked(rowData)"
                      [binary]="true"
                      [indeterminate]="isGroupIndeterminate(rowData)"
                      (onChange)="toggleGroup(rowData)"
                      [ariaLabel]="rowData.name"
                    />
                  } @else {
                    <p-checkbox
                      [ngModel]="isSelected(rowData.key)"
                      [binary]="true"
                      (onChange)="toggleNode(rowData.key)"
                      [ariaLabel]="rowData.name"
                    />
                  }
                }
              </td>
              <td>
                <div class="flex items-center gap-2">
                  <p-treetabletoggler [rowNode]="rowNode" />
                  <div class="flex flex-col">
                    <span>{{ rowData.name }}</span>
                    <span class="text-xs text-muted-color">{{
                      rowData.typeLabel
                    }}</span>
                  </div>
                </div>
              </td>
              <td class="w-1/4">
                @if (rowData.needsGrant) {
                  <span class="text-muted-color"
                    >Ressource muss noch freigegeben werden</span
                  >
                } @else {
                  <span class="flex items-center gap-1 text-bd-success">
                    <i class="pi pi-check"></i>
                    {{ rowData.permissionLabel }}
                  </span>
                }
              </td>
            </tr>
          </ng-template>
        </p-treetable>
      }
    </div>
  `,
  imports: [FormsModule, TreeTableModule, Checkbox],
})
export class BdGrantPreviewStepComponent {
  public readonly items = input<SelectedItem[]>([]);
  public readonly grantConfig = input<GrantConfig | undefined>(undefined);
  public readonly previewReady = output<boolean>();
  public readonly selectionChange = output<GrantSelection>();

  public readonly nodes = signal<TreeNode<PreviewNodeData>[]>([]);

  /**
   * `'forbidden'` when the share-preview returned 403 (the user lacks DELETE on
   * the resource), `'error'` for any other load failure, `'none'` otherwise.
   * Drives a clear message instead of an empty, non-advanceable table
   * (BDA-5507).
   */
  public readonly errorState = signal<'none' | 'forbidden' | 'error'>('none');

  private readonly _selectedKeys = signal<ReadonlySet<string>>(new Set());

  private readonly _actionableKeys = computed(() => {
    const keys = new Set<string>();
    for (const node of this.nodes()) {
      this._collectActionableKeys(node, keys);
    }
    return keys;
  });

  public readonly totalCount = computed(() => this._actionableKeys().size);

  public readonly selectedCount = computed(() => {
    const selected = this._selectedKeys();
    let count = 0;
    for (const key of this._actionableKeys()) {
      if (selected.has(key)) {
        count++;
      }
    }
    return count;
  });

  public readonly allSelected = computed(
    () => this.totalCount() > 0 && this.selectedCount() === this.totalCount(),
  );

  public readonly masterIndeterminate = computed(
    () => this.selectedCount() > 0 && !this.allSelected(),
  );

  private readonly _resourcesService = inject(BdShareablesService);

  constructor() {
    effect(() => {
      const items = this.items();
      const config = this.grantConfig();
      if (items.length === 0 || !config) {
        this.nodes.set([]);
        this._selectedKeys.set(new Set());
        this.errorState.set('none');
        return;
      }
      this._loadPreviews(items, config);
    });
  }

  public isSelected(key: string): boolean {
    return this._selectedKeys().has(key);
  }

  public isGroupChecked(data: PreviewNodeData): boolean {
    const selected = this._selectedKeys();
    return (
      data.groupKeys.length > 0 &&
      data.groupKeys.every((key) => selected.has(key))
    );
  }

  public isGroupIndeterminate(data: PreviewNodeData): boolean {
    const selected = this._selectedKeys();
    const selectedInGroup = data.groupKeys.filter((key) =>
      selected.has(key),
    ).length;
    return selectedInGroup > 0 && selectedInGroup < data.groupKeys.length;
  }

  public toggleNode(key: string): void {
    const next = new Set(this._selectedKeys());
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this._applySelection(next);
  }

  public toggleGroup(data: PreviewNodeData): void {
    const next = new Set(this._selectedKeys());
    const select = !this.isGroupChecked(data);
    for (const key of data.groupKeys) {
      if (select) {
        next.add(key);
      } else {
        next.delete(key);
      }
    }
    this._applySelection(next);
  }

  public toggleAll(): void {
    const next = this.allSelected()
      ? new Set<string>()
      : new Set(this._actionableKeys());
    this._applySelection(next);
  }

  private _applySelection(keys: ReadonlySet<string>): void {
    this._selectedKeys.set(keys);
    this._emitSelection();
  }

  private _emitSelection(): void {
    const selected = this._selectedKeys();
    const rootIds: string[] = [];
    const rootKeys = new Set<string>();
    for (const node of this.nodes()) {
      const data = node.data;
      if (!data) {
        continue;
      }
      rootKeys.add(data.key);
      if (selected.has(data.key)) {
        rootIds.push(data.id);
      }
    }

    const dependencies: GrantSelection['dependencies'] = [];
    const seen = new Set<string>();
    for (const node of this.nodes()) {
      for (const child of node.children ?? []) {
        const data = child.data;
        if (
          !data?.actionable ||
          !selected.has(data.key) ||
          rootKeys.has(data.key) ||
          seen.has(data.key)
        ) {
          continue;
        }
        seen.add(data.key);
        dependencies.push({ id: data.id, type: data.type, name: data.name });
      }
    }

    this.selectionChange.emit({ rootIds, dependencies });
  }

  private _collectActionableKeys(
    node: TreeNode<PreviewNodeData>,
    keys: Set<string>,
  ): void {
    if (node.data?.actionable) {
      keys.add(node.data.key);
    }
    for (const child of node.children ?? []) {
      this._collectActionableKeys(child, keys);
    }
  }

  private _loadPreviews(items: SelectedItem[], config: GrantConfig): void {
    this.errorState.set('none');
    const previews$ = items.map((item) =>
      this._resourcesService
        .sharePreview(item.type, item.id, config.granteeId, config.granteeType)
        .pipe(take(1)),
    );
    if (previews$.length === 0) {
      this.nodes.set([]);
      this._selectedKeys.set(new Set());
      this.previewReady.emit(true);
      return;
    }
    forkJoin(previews$).subscribe({
      next: (previews) => {
        const nodes = previews.map((preview) => this._toNode(preview));
        this.nodes.set(nodes);
        this._selectDefaults(nodes);
        this.previewReady.emit(true);
      },
      error: (err: unknown) => {
        this.nodes.set([]);
        this._selectedKeys.set(new Set());
        this.errorState.set(
          err instanceof HttpErrorResponse && err.status === 403
            ? 'forbidden'
            : 'error',
        );
        this.previewReady.emit(false);
      },
    });
  }

  private _selectDefaults(nodes: TreeNode<PreviewNodeData>[]): void {
    const keys = new Set<string>();
    for (const node of nodes) {
      this._collectActionableKeys(node, keys);
    }
    this._selectedKeys.set(keys);
    this._emitSelection();
  }

  private _toNode(preview: SharePreview): TreeNode<PreviewNodeData> {
    const permissionNames = BD_SHARE_PERMISSION_DISPLAY_NAME['de'];
    const shareable = preview.shareable;
    const rootKey = nodeKey(shareable.type, shareable.id);

    const children = preview.dependencies.map((dep) => {
      const needsGrant = dep.currentPermissions.length === 0;
      return {
        data: {
          key: nodeKey(dep.type, dep.id),
          id: dep.id,
          type: dep.type,
          name: dep.name ?? dep.id,
          typeLabel: SHAREABLE_TYPE_DISPLAY_NAME[dep.type] ?? dep.type,
          permissionLabel: this._formatPermissions(dep, permissionNames),
          needsGrant,
          isRoot: false,
          // Dependencies that already have access are not actionable.
          actionable: needsGrant,
          groupKeys: [],
        },
      } satisfies TreeNode<PreviewNodeData>;
    });

    const groupKeys = [
      rootKey,
      ...children.filter((c) => c.data.actionable).map((c) => c.data.key),
    ];

    return {
      data: {
        key: rootKey,
        id: shareable.id,
        type: shareable.type,
        name: shareable.name ?? shareable.id,
        typeLabel:
          SHAREABLE_TYPE_DISPLAY_NAME[shareable.type] ?? shareable.type,
        permissionLabel: this._formatPermissions(shareable, permissionNames),
        needsGrant: shareable.currentPermissions.length === 0,
        isRoot: true,
        // Originally selected items are always grantable.
        actionable: true,
        groupKeys,
      },
      expanded: true,
      children,
    };
  }

  private _formatPermissions(
    resource: SharePreviewResource,
    permissionNames: Record<string, string>,
  ): string {
    if (resource.currentPermissions.length === 0) {
      return 'Kein Zugriff';
    }
    const highest = BdSharePermissionHelper.getHighestPermission(
      resource.currentPermissions,
    );
    return permissionNames[highest] ?? highest;
  }
}
